import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';

interface StatsStripProps {
  total: number;
  storageLabel: string;
  byType: Record<string, number>;
  linkedCount: number;
  unlinkedCount: number;
  pageScopeOnly: boolean;
}

// When paginated, storage / linked / unlinked / by-type aggregate the current
// page only — server doesn't compute them across the full set yet. Show the
// caveat instead of letting users mistake page-scoped numbers for totals.
const PAGE_SCOPE_NOTE = '(this page)';

export const StatsStrip = ({
  total,
  storageLabel,
  byType,
  linkedCount,
  unlinkedCount,
  pageScopeOnly,
}: StatsStripProps) => {
  const scopeNote = pageScopeOnly ? ` ${PAGE_SCOPE_NOTE}` : '';
  return (
    <div className='tw:grid tw:gap-3 tw:grid-cols-2 tw:md:grid-cols-4'>
      <Card>
        <CardContent className='tw:p-4'>
          <div className='tw:text-[11px] tw:uppercase tw:tracking-wider tw:text-ThemeTextLight tw:font-semibold'>
            Total documents
          </div>
          <div className='tw:text-2xl tw:font-semibold tw:mt-1'>{total}</div>
          <div className='tw:text-xs tw:text-ThemeTextMedium tw:mt-1'>
            {storageLabel} used{scopeNote}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className='tw:p-4'>
          <div className='tw:text-[11px] tw:uppercase tw:tracking-wider tw:text-ThemeTextLight tw:font-semibold'>
            Linked to activities{scopeNote}
          </div>
          <div className='tw:text-2xl tw:font-semibold tw:mt-1'>
            {linkedCount}
            <span className='tw:text-sm tw:text-ThemeTextLight tw:font-normal'>
              {' '}
              / {linkedCount + unlinkedCount}
            </span>
          </div>
          <div className='tw:text-xs tw:text-ThemeTextMedium tw:mt-1'>audit-ready</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className='tw:p-4'>
          <div className='tw:text-[11px] tw:uppercase tw:tracking-wider tw:text-ThemeTextLight tw:font-semibold'>
            Unlinked{scopeNote}
          </div>
          <div
            className={`tw:text-2xl tw:font-semibold tw:mt-1 ${
              unlinkedCount > 0 ? 'tw:text-amber-600' : ''
            }`}
          >
            {unlinkedCount}
          </div>
          <div className='tw:text-xs tw:text-ThemeTextMedium tw:mt-1'>
            {unlinkedCount > 0 ? 'review & attach' : 'all covered'}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className='tw:p-4'>
          <div className='tw:text-[11px] tw:uppercase tw:tracking-wider tw:text-ThemeTextLight tw:font-semibold tw:mb-2'>
            By type{scopeNote}
          </div>
          {Object.keys(byType).length === 0 ? (
            <div className='tw:text-xs tw:text-ThemeTextLight'>No documents yet</div>
          ) : (
            <div className='tw:flex tw:flex-wrap tw:gap-1.5'>
              {Object.entries(byType).map(([ext, count]) => (
                <Badge key={ext} variant='ghost' className='tw:text-[11px]'>
                  <span className='tw:mr-1 tw:font-mono tw:text-[10px]'>{ext}</span>
                  <strong>{count}</strong>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
